/**
 * SEGMENTED CONTROL COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - iOS-style segmented control
 * - Animated pill indicator
 * - Glass material background
 * - Icon support
 * - Fluid morphing transitions
 */

import { useState, useRef, useEffect, ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../lib/utils'

interface SegmentedControlOption<T extends string> {
  value: T
  label: string
  icon?: ReactNode
  disabled?: boolean
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedControlOption<T>[]
  value: T
  onChange: (value: T) => void
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  className?: string
}

const sizeClasses = {
  sm: {
    container: 'p-1 rounded-lg',
    item: 'px-3 py-1.5 text-footnote rounded-md',
    icon: 'w-4 h-4',
  },
  md: {
    container: 'p-1 rounded-xl',
    item: 'px-4 py-2 text-subheadline rounded-lg',
    icon: 'w-5 h-5',
  },
  lg: {
    container: 'p-1.5 rounded-2xl',
    item: 'px-5 py-2.5 text-body rounded-xl',
    icon: 'w-5 h-5',
  },
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  fullWidth = false,
  className,
}: SegmentedControlProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number
    width: number
  }>({ left: 0, width: 0 })

  const sizes = sizeClasses[size]

  // Update indicator position when value changes
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const activeIndex = options.findIndex(opt => opt.value === value)
    const buttons = container.querySelectorAll('button')
    const activeButton = buttons[activeIndex]

    if (activeButton) {
      const containerRect = container.getBoundingClientRect()
      const buttonRect = activeButton.getBoundingClientRect()

      setIndicatorStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
      })
    }
  }, [value, options])

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative inline-flex',
        'liquid-material-clear',
        sizes.container,
        fullWidth && 'w-full',
        className
      )}
    >
      {/* Animated pill indicator */}
      <motion.div
        className={cn(
          'absolute top-1 bottom-1',
          'liquid-material-regular',
          size === 'sm' && 'rounded-md',
          size === 'md' && 'rounded-lg',
          size === 'lg' && 'rounded-xl',
          'shadow-liquid-sm dark:shadow-dark-liquid-sm'
        )}
        initial={false}
        animate={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30,
          mass: 0.8,
        }}
        style={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }}
      />

      {/* Options */}
      {options.map((option) => {
        const isActive = option.value === value
        const isDisabled = option.disabled

        return (
          <button
            key={option.value}
            onClick={() => !isDisabled && onChange(option.value)}
            disabled={isDisabled}
            className={cn(
              'relative z-10 flex items-center justify-center gap-2',
              'font-medium tracking-tight',
              'transition-colors duration-200 ease-out',
              sizes.item,
              fullWidth && 'flex-1',

              // Active state
              isActive
                ? 'text-light-text-primary dark:text-dark-text-primary'
                : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-secondary dark:hover:text-dark-text-secondary',

              // Disabled state
              isDisabled && 'opacity-40 cursor-not-allowed'
            )}
          >
            {/* Icon */}
            {option.icon && (
              <span className={cn(sizes.icon, 'flex-shrink-0')}>
                {option.icon}
              </span>
            )}

            {/* Label */}
            <span>{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * TabControl - Variant for tab navigation
 */
interface TabControlProps<T extends string> extends SegmentedControlProps<T> {
  underline?: boolean
}

export function TabControl<T extends string>({
  options,
  value,
  onChange,
  underline = false,
  className,
}: TabControlProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number
    width: number
  }>({ left: 0, width: 0 })

  // Update indicator position
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const activeIndex = options.findIndex(opt => opt.value === value)
    const buttons = container.querySelectorAll('button')
    const activeButton = buttons[activeIndex]

    if (activeButton) {
      const containerRect = container.getBoundingClientRect()
      const buttonRect = activeButton.getBoundingClientRect()

      setIndicatorStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
      })
    }
  }, [value, options])

  if (underline) {
    return (
      <div
        ref={containerRef}
        className={cn('relative flex border-b border-light-separator dark:border-dark-separator', className)}
      >
        {/* Underline indicator */}
        <motion.div
          className="absolute bottom-0 h-0.5 bg-blue dark:bg-blue-dark rounded-full"
          initial={false}
          animate={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30,
          }}
        />

        {/* Options */}
        {options.map((option) => {
          const isActive = option.value === value

          return (
            <button
              key={option.value}
              onClick={() => onChange(option.value)}
              disabled={option.disabled}
              className={cn(
                'relative px-4 py-3 text-subheadline font-medium tracking-tight',
                'transition-colors duration-200',
                isActive
                  ? 'text-blue dark:text-blue-dark'
                  : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-secondary dark:hover:text-dark-text-secondary',
                option.disabled && 'opacity-40 cursor-not-allowed'
              )}
            >
              {option.icon && (
                <span className="mr-2 inline-flex">{option.icon}</span>
              )}
              {option.label}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <SegmentedControl
      options={options}
      value={value}
      onChange={onChange}
      className={className}
    />
  )
}

/**
 * IconSegmentedControl - Segmented control with icons only
 */
interface IconSegmentedControlOption<T extends string> {
  value: T
  icon: ReactNode
  label: string // Used for accessibility
  disabled?: boolean
}

interface IconSegmentedControlProps<T extends string> {
  options: IconSegmentedControlOption<T>[]
  value: T
  onChange: (value: T) => void
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const iconSizeClasses = {
  sm: {
    container: 'p-1 rounded-lg gap-0.5',
    item: 'w-8 h-8 rounded-md',
    icon: 'w-4 h-4',
  },
  md: {
    container: 'p-1 rounded-xl gap-0.5',
    item: 'w-10 h-10 rounded-lg',
    icon: 'w-5 h-5',
  },
  lg: {
    container: 'p-1.5 rounded-2xl gap-1',
    item: 'w-12 h-12 rounded-xl',
    icon: 'w-6 h-6',
  },
}

export function IconSegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  className,
}: IconSegmentedControlProps<T>) {
  const sizes = iconSizeClasses[size]

  return (
    <div
      className={cn(
        'inline-flex',
        'liquid-material-clear',
        sizes.container,
        className
      )}
    >
      {options.map((option) => {
        const isActive = option.value === value

        return (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            disabled={option.disabled}
            aria-label={option.label}
            className={cn(
              'relative flex items-center justify-center',
              'transition-all duration-200 ease-out',
              sizes.item,

              isActive
                ? 'liquid-material-regular text-light-text-primary dark:text-dark-text-primary shadow-liquid-sm'
                : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-secondary dark:hover:text-dark-text-secondary hover:bg-light-bg-tertiary/50 dark:hover:bg-dark-bg-tertiary/50',

              option.disabled && 'opacity-40 cursor-not-allowed'
            )}
          >
            <span className={sizes.icon}>{option.icon}</span>
          </button>
        )
      })}
    </div>
  )
}
